import { styled } from "goober";
import { useId } from "react";
import { smoothPath, type Point } from "../lib/smoothPath";

const Svg = styled("svg")`
  display: block;
  overflow: visible;
`;

type Props = {
  values: readonly number[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
};

export function Sparkline({
  values,
  width = 104,
  height = 36,
  color = "var(--text-secondary)",
  strokeWidth = 1.5,
}: Props) {
  const uid = useId();
  const gradId = `${uid}-fill`;
  if (values.length < 2) return null;

  const max = Math.max(...values, 1);
  const top = strokeWidth;
  const usableHeight = height - strokeWidth * 2;
  const points: Point[] = values.map((value, index) => ({
    x: (index / (values.length - 1)) * width,
    y: top + usableHeight * (1 - value / max),
  }));

  const curve = smoothPath(points);
  const area = `${curve}L${points[points.length - 1]!.x},${height}L${points[0]!.x},${height}Z`;

  return (
    <Svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="60%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path
        d={curve}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </Svg>
  );
}
